<template>
  <component
    :is="element"
    class="button"
    :class="[
      modifierClasses,
      {
        'no-icon': !showIcon,
        'has-hover-icon': $slots.hoverIcon,
        'show-state-icon': showStateIcon,
        [$class('disabled')]: isDisabled
      },
    ]"
    :href="url || null"
    :type="type || null"
  >
    <span
      class="button__label"
      :class="labelClasses"
      v-text="label"
    />

    <span
      v-if="showPricing()"
      class="button__label-price-divider"
      v-text="$string('product.cta.price_divider')"
    />

    <product-prices
      v-if="showPricing()"
      class="
        button__label
        button__label--price
        button__add-to-cart-prices
      "
      :available="prices.available"
      block-class="main-product"
      element="span"
      :compare-at-price="showSlashedPrice ? prices.compareAtPrice : false"
      class-compare-at-price="text-other-sale-s"
      :class-price="customPriceClass || 'text-body-s-bold'"
      :price="prices.price"
    />

    <span
      v-if="showIcon || $slots.hoverIcon"
      class="button__icon"
    >
      <span
        v-if="showIcon"
        class="button__default-icon"
      >
        <slot name="icon">
          <icon-arrow-right />
        </slot>
      </span>

      <span
        v-if="$slots.hoverIcon"
        class="button__hover-icon"
      >
        <slot name="hoverIcon" />
      </span>
    </span>

    <span
      v-if="showStateIcon && $slots.stateIcon"
      class="button__state-icon"
    >
      <slot name="stateIcon" />
    </span>
  </component>
</template>

<script>

/**
 * Vue: Button (btn)
 * -----------------------------------------------------------------------------
 * Global interactive button element.
 * - Styles are imported in critical styles.
 * - Must be imported as <btn> as <button> is a default element.
 *
 * @param {String} [classLabel] - Label class.
 * @param {String} [customElement] - Optional override to set button element.
 * @param {String} label - Button label.
 * @param {String} [modifiers] - Button modifiers in space separated list
 * (primary, secondary, fill, text, dark, block, center, or reversed).
 * @param {Boolean} isDisabled - Adds the is-disabled class to the button.
 * @param {Boolean} prices.available - Shopify-provided availability of a given
 * product.
 * @param {Boolean} prices.compareAtPrice - Compare-at Price from CANVAS
 * formatted product object.
 * @param {Boolean} prices.price - Price from CANVAS formatted product object.
 * @param {Boolean} [showSlashedPrice] - Show the slashed pricing.
 * @param {Boolean} [showOutOfStockPricing] - Show the pricing when out of
 * stock.
 * @param {Boolean} [showIcon] - Whether to show the default icon, hover icons
 * will still be displayed if passed into their slot.
 * @param {Boolean} [showStateIcon] - Show secondary button state, replaces text
 * and default icon with `stateIcon` slot.
 * @param {String} [type] - Type of button (button or submit).
 * @param {String} [url] - URL if link.
 *
 */
import ProductPrices from '~global/product-prices/product-prices'

import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Btn',

  components: {
    ProductPrices,
    IconArrowRight,
  },

  props: {
    classLabel: {
      type: String,
      default: '',
    },
    customElement: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    modifiers: {
      type: [String, Array],
      default: 'primary block center',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    prices: {
      type: Object,
      default: () => ({
        available: false,
        compareAtPrice: false,
        price: false,
      }),
    },
    showSlashedPrice: {
      type: Boolean,
      default: true,
    },
    showOutOfStockPricing: {
      type: Boolean,
      default: true,
    },
    showPricingText: {
      type: Boolean,
      default: false,
    },
    customPriceClass: {
      type: [Boolean, String],
      default: false,
    },
    showIcon: {
      type: Boolean,
      default: false,
    },
    showStateIcon: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
      validator: (value) => {
        return ['', 'button', 'submit'].includes(value)
      },
    },
    url: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      if (this.customElement) {
        return this.customElement
      }

      return this.url ? 'a' : 'button'
    },

    /**
     * Compute label classes.
     * @returns {String}
     */
    labelClasses() {
      if (this.classLabel) {
        return this.classLabel
      }

      if (this.modifiers.includes('text')) {
        return 'text-body-s-regular'
      }

      return 'text-body-s-regular'
    },

    /**
     * Compute modifier classes.
     * @returns {String}
     */
    modifierClasses() {
      if (!this.modifiers) {
        return ''
      }

      let modifiers = []

      if (Array.isArray(this.modifiers)) {
        modifiers = this.modifiers
      } else {
        modifiers = this.modifiers.split(/[\s,]/g)
      }

      return modifiers.map((modifier) => {
        return `button--${modifier}`
      }).join(' ')
    },
  },

  methods: {

    /**
     * Determine whether to show the pricing and pricing divider.
     * @returns {Boolean}
     */
    showPricing() {
      if (!this.prices?.price || !this.showPricingText) {
        return false
      }

      if (this.showStateIcon && this.$slots.stateIcon) {
        return false
      }

      if (!this.prices.available && !this.showOutOfStockPricing) {
        return false
      }

      return true
    },
  },
}
</script>
